import { useEffect, useState } from "react";
import type { Message } from "@swift/shared";
import { api } from "../lib/api";
import { useStore } from "../lib/store";
import { renderMarkdown } from "../lib/markdown";
import { scrollToMessage } from "../lib/scrollToMessage";

/**
 * Lade met alle berichten waarin jij @-mentioned bent — als losse
 * @username, of als geëxpandeerde @here / @channel. Wordt geopend via
 * de @-knop in de StatusBar; klik op een rij springt naar de juiste
 * conversatie en scrollt naar het bericht (laadt zo nodig oudere
 * pagina's, net als de pin-strook).
 */
export default function MentionsDrawer() {
  const open = useStore((s) => s.mentionsOpen);
  const close = useStore((s) => s.closeMentions);
  const conversations = useStore((s) => s.conversations);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const [items, setItems] = useState<Message[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    void (async () => {
      try {
        const r = await api.listMyMentions(50);
        setItems(r.messages);
      } catch {
        setError("ophalen mislukt");
      }
    })();
  }, [open]);

  if (!open) return null;

  function convLabel(conversationId: number): string {
    const c = conversations[conversationId];
    if (!c) return `Gesprek #${conversationId}`;
    if (c.kind === "channel") return `#${c.name ?? "kanaal"}`;
    const otherId = c.member_ids.find((id) => id !== me?.id);
    return otherId !== undefined
      ? users[otherId]?.display_name ?? `Gesprek #${conversationId}`
      : "Gesprek";
  }

  async function jumpTo(message: Message) {
    close();
    await scrollToMessage({
      conversationId: message.conversation_id,
      messageId: message.id,
      switchActive: true,
    });
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 pt-12"
      onClick={close}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-700 bg-slate-800 px-4 py-2">
          <span className="text-accent">@</span>
          <h2 className="flex-1 text-sm font-medium text-slate-200">
            Mijn vermeldingen
          </h2>
          <button
            onClick={close}
            className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {items === null && !error && (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              Bezig met laden…
            </div>
          )}
          {error && (
            <div className="px-4 py-6 text-center text-sm text-red-400">{error}</div>
          )}
          {items && items.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              Je bent nog niet genoemd. Verschijn in een conversatie en wacht
              tot iemand <span className="text-accent">@{me?.username}</span> typt.
            </div>
          )}
          {items && items.length > 0 && (
            <ul>
              {items.map((m) => {
                const sender =
                  users[m.sender_id]?.display_name ?? `Gebruiker #${m.sender_id}`;
                const time = new Date(m.created_at).toLocaleString("nl-NL", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                });
                return (
                  <li key={m.id} className="border-b border-slate-700/60 last:border-b-0">
                    <button
                      onClick={() => void jumpTo(m)}
                      className="flex w-full items-start gap-3 px-4 py-2 text-left hover:bg-slate-800"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 text-xs">
                          <span className="font-medium text-accent">
                            {convLabel(m.conversation_id)}
                          </span>
                          <span className="text-slate-400">{sender}</span>
                          <span className="ml-auto shrink-0 text-slate-500">{time}</span>
                        </div>
                        <div
                          className="line-clamp-3 text-sm text-slate-200"
                          dangerouslySetInnerHTML={{
                            __html: renderMarkdown(m.body, {
                              highlightSelf: me?.username,
                            }),
                          }}
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
